import './address.scss';

import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Calendar } from 'react-calendar';
import { IoArrowBackCircle } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import ListSchedules from '@/resources/components/global/address/components/ListSchedules';
import { getAllDeliveryIntegrations } from '@/routes/integrations';
import { ApplicationState } from '@/store';
import { IIntegration } from '@/store/ducks/integrations/types';

import { getAddresses, IAddressResponse } from '../../../../routes/addresses';
import { ClientTypes, isFinalCustomer } from '../../../../services/clients';
import { useWebContext } from '../../../hooks/useWebContext';
import CustomModal from '../CustomModal';
import ListAddresses from './components/ListAddresses';
import ListDeliveryMethods from './components/ListDeliveryMethods';
import ListStores from './components/ListStores';
import SearchAddress from './components/SearchAddress';
import { IAddressModalStep } from './components/types';

interface Props {
  showModal: boolean;
  step: IAddressModalStep;
  showSchedule: boolean;
  handleClose: () => void;
  setShowModal: (show: boolean) => void;
}

function AddressModal({ showModal, step, showSchedule, handleClose, setShowModal }: Props) {
  const { order } = useWebContext();

  if (!order) return <></>;

  const { id: orderId, client, shipment, dst_shop_id } = order || {};

  const { setShowIdenfificationModal } = useWebContext();
  const { finalCustomer } = isFinalCustomer(client.type);

  const [modalStep, setModalStep] = useState<IAddressModalStep>('type');

  const [transitioning, setTransitioning] = useState<boolean>(false);

  const [show, setShow] = useState<boolean>(showModal);
  const [calendarDate, setCalendarDate] = useState<Date | null>(null);

  const [clientAddresses, setClientAddresses] = useState<IAddressResponse[]>([]);

  const [displayTitle, setDisplayTitle] = useState('');

  const selectIntegrations = useSelector<ApplicationState, IIntegration[]>(
    (state) => state.integrations.data,
  );

  const getStoreIntegrations = () => {
    return selectIntegrations.filter((integration) => integration.type === 'in-store');
  };

  const handleModalChange = (step: IAddressModalStep) => {
    const largeHeightSteps: IAddressModalStep[] = ['scheduling', 'calendar', 'manualAddAddress'];

    const currentIsLarge = largeHeightSteps.includes(modalStep);
    const nextIsLarge = largeHeightSteps.includes(step);
    const shouldBounce = currentIsLarge !== nextIsLarge; // Height change detected

    setTransitioning(shouldBounce);
    setModalStep(step);

    setTimeout(
      () => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        setDisplayTitle(renderModalTitleByStep(step));
        setTransitioning(false);
      },
      shouldBounce ? 250 : 0,
    );
  };

  const handleShowSchedule = (deliveryType?: string, validAddress: boolean = true) => {
    const isToShow = deliveryType === 'google-maps' || !deliveryType;

    if (showSchedule && isToShow && validAddress) handleModalChange('scheduling');
    else {
      handleModalChange('type');
      setShowModal(false);
    }
  };

  const setShipmentType = (type: 'delivery' | 'shop') => {
    if (type === 'delivery' && client.type === ClientTypes.ANONYMOUS) {
      toast('Indentifique-se para escolher essa opção!', { type: 'warning' });
      setShowModal(false);
      setShowIdenfificationModal(true);

      return;
    }

    if (type === 'delivery') handleModalChange('address');
    if (type === 'shop') handleModalChange('stores');
  };

  function renderModalTitleByStep(step: IAddressModalStep) {
    switch (step) {
      case 'type':
        return 'Escolha a forma de entrega';
      case 'stores':
        return 'Escolha a loja de retirada';
      case 'address':
        return 'Delivery';
      case 'addAddress':
      case 'manualAddAddress':
        return 'Adicionar novo endereço';
      case 'scheduling':
        return 'Agende o horário de entrega';
      default:
        return '';
    }
  }

  const handleCalendarChange = (someDate: Date) => {
    const day = someDate.toISOString().split('T').shift();
    const timeNow = new Date().toTimeString().split(':');
    const date = new Date(`${day}T${timeNow[0]}:${timeNow[1]}`);

    setCalendarDate(date);
    handleModalChange('scheduling');
  };

  const renderModalContent = (step: IAddressModalStep) => {
    switch (step) {
      case 'type':
        return (
          <div className="p-2">
            <ListDeliveryMethods
              integrations={selectIntegrations}
              setShipmentType={setShipmentType}
            />
          </div>
        );
      case 'address':
        return (
          <ListAddresses addresses={clientAddresses} handleShowSchedule={handleShowSchedule} />
        );
      case 'addAddress':
      case 'manualAddAddress':
        return (
          <div className="p-2">
            <SearchAddress
              orderId={orderId}
              clientId={client.id}
              setClientAddresses={setClientAddresses}
              setModalStep={handleModalChange}
              step={step}
            />
          </div>
        );
      case 'stores':
        return (
          <ListStores
            integrations={getStoreIntegrations()}
            handleShowSchedule={handleShowSchedule}
          />
        );
      case 'scheduling':
        return (
          <ListSchedules
            orderId={orderId}
            shopIdDst={dst_shop_id}
            shipment={shipment}
            calendarDate={calendarDate}
            setShowModal={setShowModal}
            setModalStep={handleModalChange}
          />
        );
      case 'calendar':
        return (
          <div className="p-2">
            <div className="d-flex justify-content-center address-calendar">
              <Calendar
                onChange={(value: Date) => handleCalendarChange(value)}
                value={calendarDate}
                minDate={new Date()}
              />
            </div>
          </div>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (client.id && !finalCustomer && showModal)
      getAddresses(client.id).then((addresses) => setClientAddresses(addresses));
  }, [client.id, finalCustomer, showModal]);

  useEffect(() => setShow(showModal), [showModal]);

  useEffect(() => handleModalChange(step), [step]);

  useEffect(() => {
    if (step === 'type' && showModal) {
      getAllDeliveryIntegrations(orderId);
    }
  }, [showModal]);

  const arrowActionByStep = {
    address: () => handleModalChange('type'),
    addAddress: () => handleModalChange('address'),
    manualAddAddress: () => handleModalChange('address'),
    calendar: () => handleModalChange('scheduling'),
    stores: () => handleModalChange('type'),
    scheduling: (type: string | null) =>
      type === 'shop' ? handleModalChange('stores') : handleModalChange('address'),
  };

  return (
    <CustomModal
      show={show}
      animation={false}
      centered={true}
      className={`modal-smooth-resize ${transitioning ? 'height-changing' : ''}`}
      onHide={() => {
        handleClose();
        setShowModal(false);
        handleModalChange('type');
      }}
    >
      <Modal.Header closeButton className="p-2 d-flex align-items-center">
        <div className="d-flex w-100 justify-content-between align-items-center">
          <div className="d-flex w-100 align-items-center py-2">
            {modalStep !== 'type' && (
              <IoArrowBackCircle
                size="18pt"
                color="#333"
                className="mr-1 cursor-pointer"
                onClick={() => {
                  arrowActionByStep[modalStep](shipment.type);
                }}
              />
            )}

            <span
              className={`mb-0 ${transitioning ? 'title-transition' : ''}`}
              style={{ fontSize: '1.125rem', fontWeight: 700 }}
            >
              {displayTitle}
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body
        className={`${modalStep === 'address' || modalStep === 'stores' ? 'px-0' : 'p-2'}`}
      >
        <>{renderModalContent(modalStep)}</>
      </Modal.Body>

      {modalStep === 'address' && (
        <Modal.Footer className="p-2 border-top-0">
          <Button
            variant="success"
            size="sm"
            className="w-100"
            onClick={() => {
              handleModalChange('manualAddAddress');
            }}
          >
            Adicionar novo endereço
          </Button>
        </Modal.Footer>
      )}
    </CustomModal>
  );
}

export default AddressModal;
